import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { 
    ArrowLeft, Pill, Download, Loader2, CheckCircle2, Clock, 
    MapPin, Truck, FileText, AlertTriangle, Phone, Building2
} from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const deliveryOptions = [
    { 
        id: 'self_pickup', 
        name: 'Self Pickup', 
        icon: FileText, 
        description: 'Download prescription PDF and take to any pharmacy',
        price: 'Free'
    },
    { 
        id: 'pharmacy_delivery', 
        name: 'Pharmacy Delivery', 
        icon: Truck, 
        description: 'Send to pharmacy for home delivery (for incapacitated users)',
        price: '+$10 delivery fee'
    }
];

const statusColors = {
    pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    approved: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    dispensed: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    delivered: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
    rejected: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
};

export default function Prescriptions() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [prescriptions, setPrescriptions] = useState([]);
    const [showRequest, setShowRequest] = useState(false);
    const [formData, setFormData] = useState({
        delivery_type: 'self_pickup',
        medication_request: '',
        symptoms: '',
        allergies: '',
        current_medications: '',
        // Delivery details
        delivery_address: '',
        delivery_phone: '',
        pharmacy_name: '',
        pharmacy_address: '',
        special_instructions: ''
    });

    useEffect(() => {
        fetchPrescriptions();
    }, []);

    const fetchPrescriptions = async () => {
        try {
            const response = await axios.get(`${API}/prescriptions`);
            setPrescriptions(response.data);
        } catch (err) {
            console.error('Failed to fetch prescriptions');
        }
    };

    const handleSubmit = async () => {
        if (!formData.medication_request || !formData.symptoms) {
            toast.error('Please describe your condition and medication needs');
            return;
        }

        if (formData.delivery_type === 'pharmacy_delivery') {
            if (!formData.delivery_address || !formData.delivery_phone) {
                toast.error('Please fill in delivery address and phone number');
                return;
            }
        }

        setLoading(true);
        try {
            await axios.post(`${API}/prescriptions/request`, formData);
            toast.success('Prescription request submitted! A doctor will review it shortly.');
            setShowRequest(false);
            setFormData({
                delivery_type: 'self_pickup',
                medication_request: '',
                symptoms: '',
                allergies: '',
                current_medications: '',
                delivery_address: '',
                delivery_phone: '',
                pharmacy_name: '',
                pharmacy_address: '',
                special_instructions: ''
            });
            fetchPrescriptions();
        } catch (err) {
            if (err.response?.status === 403) {
                toast.error('Premium subscription required for prescriptions');
                navigate('/subscription');
            } else {
                toast.error('Failed to submit prescription request');
            }
        } finally {
            setLoading(false);
        }
    };

    const downloadPrescription = (prescription) => {
        const doc = new jsPDF();
        
        // Header with logo
        doc.setFontSize(22);
        doc.setTextColor(20, 184, 166);
        doc.text('MediGuide', 20, 20);
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text('AI-Powered Health Assessment', 20, 27);
        
        // Prescription label
        doc.setFillColor(20, 184, 166);
        doc.rect(140, 12, 55, 18, 'F');
        doc.setFontSize(12);
        doc.setTextColor(255);
        doc.text('PRESCRIPTION', 145, 23);
        
        // Line
        doc.setDrawColor(20, 184, 166);
        doc.setLineWidth(0.5);
        doc.line(20, 35, 190, 35);
        
        // Patient & Prescription Info
        doc.setFontSize(10);
        doc.setTextColor(0);
        doc.text(`Prescription ID: ${prescription.id}`, 20, 45);
        doc.text(`Date: ${new Date(prescription.created_at).toLocaleDateString()}`, 120, 45);
        doc.text(`Status: ${prescription.status.toUpperCase()}`, 20, 52);
        
        // Main Content
        doc.setFontSize(14);
        doc.setTextColor(20, 184, 166);
        doc.text('Rx', 20, 70);
        
        doc.setFontSize(11);
        doc.setTextColor(0);
        
        // Medications
        if (prescription.medications && prescription.medications.length > 0) {
            let yPos = 80;
            prescription.medications.forEach((med, index) => {
                doc.setFontSize(12);
                doc.text(`${index + 1}. ${med.name}`, 25, yPos);
                doc.setFontSize(10);
                doc.text(`   Dosage: ${med.dosage}`, 25, yPos + 6);
                doc.text(`   Frequency: ${med.frequency}`, 25, yPos + 12);
                doc.text(`   Duration: ${med.duration}`, 25, yPos + 18);
                if (med.instructions) {
                    doc.text(`   Instructions: ${med.instructions}`, 25, yPos + 24);
                    yPos += 32;
                } else {
                    yPos += 26;
                }
            });
        } else {
            doc.text('Medication details pending doctor review.', 25, 80);
            doc.text(`Request: ${prescription.medication_request}`, 25, 90);
        }
        
        // Symptoms
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text('Condition/Symptoms:', 20, 160);
        const symptomsSplit = doc.splitTextToSize(prescription.symptoms || 'Not specified', 170);
        doc.text(symptomsSplit, 20, 167);
        
        // Warnings
        doc.setFillColor(255, 243, 205);
        doc.rect(20, 190, 170, 25, 'F');
        doc.setFontSize(9);
        doc.setTextColor(180, 83, 9);
        doc.text('IMPORTANT:', 25, 198);
        doc.text('• This prescription is valid for 30 days from the date of issue.', 25, 205);
        doc.text('• Present this document at any licensed pharmacy.', 25, 211);
        
        // Footer
        doc.setFontSize(8);
        doc.setTextColor(100);
        doc.line(20, 250, 190, 250);
        doc.text('This prescription was issued through MediGuide AI Health Assessment platform.', 20, 256);
        doc.text('For verification, contact: support@mediguide.app | Emergency: Call your local emergency services', 20, 262);
        doc.text('MediGuide - We care about your health', 20, 268);
        
        doc.save(`MediGuide_Prescription_${prescription.id}.pdf`);
        toast.success('Prescription downloaded');
    };

    return (
        <div className="min-h-screen bg-background py-8 lg:py-12" data-testid="prescriptions-page">
            <div className="max-w-4xl mx-auto px-4">
                <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
                </Button>
                
                {/* Centered Header Section */}
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                            <Pill className="w-5 h-5 text-primary" />
                        </div>
                        <h1 className="font-heading text-3xl font-bold">Prescriptions</h1>
                    </div>
                    <p className="text-muted-foreground mb-4">Request and manage your prescriptions</p>
                    <Button onClick={() => setShowRequest(true)} className="rounded-full">
                        <Pill className="w-4 h-4 mr-2" /> Request Prescription
                    </Button>
                </div>

                {/* Info Banner */}
                <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-xl">
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-blue-800 dark:text-blue-200">
                            <p className="font-medium mb-1">How Prescriptions Work</p>
                            <ul className="space-y-1 text-blue-700 dark:text-blue-300">
                                <li>• Submit a prescription request with your symptoms</li>
                                <li>• A licensed doctor reviews and approves the prescription</li>
                                <li>• Download PDF for pharmacy pickup OR have it delivered</li>
                            </ul>
                        </div>
                    </div>
                </div>

                {showRequest && (
                    <Card className="mb-8">
                        <CardHeader>
                            <CardTitle>Request Prescription</CardTitle>
                            <CardDescription>A doctor will review your request and issue an appropriate prescription</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Delivery Type Selection */}
                            <div className="space-y-3">
                                <Label className="text-base font-medium">How would you like to receive your prescription?</Label>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    {deliveryOptions.map((option) => {
                                        const Icon = option.icon;
                                        const isSelected = formData.delivery_type === option.id;
                                        return (
                                            <button
                                                key={option.id}
                                                type="button"
                                                onClick={() => setFormData({...formData, delivery_type: option.id})}
                                                className={`p-4 rounded-xl border-2 text-left transition-all
                                                    ${isSelected 
                                                        ? 'border-primary bg-primary/5 shadow-md' 
                                                        : 'border-slate-200 dark:border-slate-700 hover:border-primary/50'}`}
                                            >
                                                <div className="flex items-center gap-3 mb-2">
                                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center
                                                        ${isSelected ? 'bg-primary text-white' : 'bg-primary/10 text-primary'}`}>
                                                        <Icon className="w-5 h-5" />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium">{option.name}</p>
                                                        <p className="text-xs text-primary">{option.price}</p>
                                                    </div>
                                                </div>
                                                <p className="text-xs text-muted-foreground">{option.description}</p>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <Label>What medication do you need? *</Label>
                                        <Textarea 
                                            value={formData.medication_request} 
                                            onChange={(e) => setFormData({...formData, medication_request: e.target.value})}
                                            placeholder="Describe the medication you need or let the doctor recommend based on your symptoms..."
                                            rows={3}
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label>Describe your symptoms *</Label>
                                        <Textarea 
                                            value={formData.symptoms} 
                                            onChange={(e) => setFormData({...formData, symptoms: e.target.value})}
                                            placeholder="Describe your current symptoms and how long you've had them..."
                                            rows={3}
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label>Known allergies</Label>
                                        <Input 
                                            value={formData.allergies} 
                                            onChange={(e) => setFormData({...formData, allergies: e.target.value})}
                                            placeholder="List any medication allergies..."
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label>Current medications</Label>
                                        <Input 
                                            value={formData.current_medications} 
                                            onChange={(e) => setFormData({...formData, current_medications: e.target.value})}
                                            placeholder="List medications you're currently taking..."
                                        />
                                    </div>
                                </div>

                                {/* Delivery Details */}
                                <div className="space-y-4">
                                    {formData.delivery_type === 'pharmacy_delivery' && (
                                        <>
                                            <div className="p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
                                                <p className="text-sm text-amber-700 dark:text-amber-300 flex items-center gap-2">
                                                    <Truck className="w-4 h-4" />
                                                    Pharmacy delivery - $10 additional fee applies
                                                </p>
                                            </div>

                                            <div className="space-y-2">
                                                <Label className="flex items-center gap-2">
                                                    <MapPin className="w-4 h-4 text-primary" />
                                                    Delivery Address *
                                                </Label>
                                                <Textarea 
                                                    value={formData.delivery_address} 
                                                    onChange={(e) => setFormData({...formData, delivery_address: e.target.value})}
                                                    placeholder="Full address including postcode..."
                                                    rows={2}
                                                />
                                            </div>

                                            <div className="space-y-2">
                                                <Label className="flex items-center gap-2">
                                                    <Phone className="w-4 h-4 text-primary" />
                                                    Contact Phone *
                                                </Label>
                                                <Input 
                                                    value={formData.delivery_phone} 
                                                    onChange={(e) => setFormData({...formData, delivery_phone: e.target.value})}
                                                    placeholder="Phone number for delivery updates..."
                                                />
                                            </div>

                                            <div className="space-y-2">
                                                <Label className="flex items-center gap-2">
                                                    <Building2 className="w-4 h-4 text-primary" />
                                                    Preferred Pharmacy (Optional)
                                                </Label>
                                                <Input 
                                                    value={formData.pharmacy_name} 
                                                    onChange={(e) => setFormData({...formData, pharmacy_name: e.target.value})}
                                                    placeholder="Pharmacy name..."
                                                />
                                            </div>

                                            <div className="space-y-2">
                                                <Label>Special Instructions</Label>
                                                <Textarea 
                                                    value={formData.special_instructions} 
                                                    onChange={(e) => setFormData({...formData, special_instructions: e.target.value})}
                                                    placeholder="Any special delivery instructions or notes for the pharmacy..."
                                                    rows={2}
                                                />
                                            </div>
                                        </>
                                    )}

                                    {formData.delivery_type === 'self_pickup' && (
                                        <div className="p-4 bg-green-50 dark:bg-green-950/30 rounded-xl border border-green-200 dark:border-green-800">
                                            <div className="flex items-start gap-3">
                                                <FileText className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                                                <div className="text-sm">
                                                    <p className="font-medium text-green-800 dark:text-green-200 mb-2">Self Pickup Selected</p>
                                                    <ul className="space-y-1 text-green-700 dark:text-green-300">
                                                        <li>• Once approved, download your prescription PDF</li>
                                                        <li>• Take it to any licensed pharmacy</li>
                                                        <li>• Present valid ID if required</li>
                                                        <li>• Prescription valid for 30 days</li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex gap-3 justify-end">
                                <Button variant="outline" onClick={() => setShowRequest(false)}>Cancel</Button>
                                <Button onClick={handleSubmit} disabled={loading} className="rounded-full">
                                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                                    Submit Request
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                <Card>
                    <CardHeader>
                        <CardTitle>Your Prescriptions</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {prescriptions.length === 0 ? (
                            <div className="text-center py-12 text-muted-foreground">
                                <Pill className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                <p>No prescriptions yet</p>
                                <Button 
                                    variant="outline" 
                                    className="mt-4"
                                    onClick={() => setShowRequest(true)}
                                >
                                    Request Your First Prescription
                                </Button>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {prescriptions.map((p) => (
                                    <div key={p.id} className="p-4 border rounded-xl">
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-start gap-4">
                                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center
                                                    ${p.delivery_type === 'pharmacy_delivery' ? 'bg-purple-100 dark:bg-purple-900/30' : 'bg-primary/10'}`}>
                                                    {p.delivery_type === 'pharmacy_delivery' ? (
                                                        <Truck className="w-5 h-5 text-purple-600" />
                                                    ) : (
                                                        <Pill className="w-5 h-5 text-primary" />
                                                    )}
                                                </div>
                                                <div>
                                                    <p className="font-medium">
                                                        {p.medication_request?.substring(0, 50)}
                                                        {p.medication_request?.length > 50 ? '...' : ''}
                                                    </p>
                                                    <p className="text-sm text-muted-foreground mt-1">
                                                        {p.delivery_type === 'pharmacy_delivery' ? 'Pharmacy Delivery' : 'Self Pickup'}
                                                    </p>
                                                    <div className="flex items-center gap-3 mt-2">
                                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[p.status]}`}>
                                                            {p.status.charAt(0).toUpperCase() + p.status.slice(1)}
                                                        </span>
                                                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                                                            <Clock className="w-3 h-3" />
                                                            {new Date(p.created_at).toLocaleDateString()}
                                                        </span>
                                                    </div>
                                                    {p.delivery_type === 'pharmacy_delivery' && p.delivery_address && (
                                                        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-2">
                                                            <MapPin className="w-3 h-3" />
                                                            {p.delivery_address.substring(0, 40)}...
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            {(p.status === 'approved' || p.status === 'dispensed') && (
                                                <Button 
                                                    variant="outline" 
                                                    size="sm"
                                                    onClick={() => downloadPrescription(p)}
                                                >
                                                    <Download className="w-4 h-4 mr-1" /> Download PDF
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
